import PropTypes from "prop-types";
import * as React from "react";
import { finalizeShopRouteInput, isShopRoute } from "../../domain/shopRoute";
import { Alert, Button, Dialog } from "../../themeV2";
import styles from "./AddShopDialog.module.css";
import { ACTION, initialState, reducer } from "./addShopReducer";
import OwnerSection from "./OwnerSection";
import PayoutSection from "./PayoutSection";
import ShippingSection from "./ShippingSection";
import ShopDetailsSection from "./ShopDetailsSection";
import ShopTypeSection from "./ShopTypeSection";
import VerticalSection from "./VerticalSection";

const isFormValid = (state) => {
  const hasShopName = state.shopName.trim().length > 0;
  const hasValidRoute = isShopRoute(finalizeShopRouteInput(state.shopRoute));
  const hasOwner = state.ownerStatus === "found" && state.ownerUser !== null;
  const hasPayoutUser =
    state.payoutSameAsOwner ||
    (state.payoutStatus === "found" && state.payoutUser !== null);

  return hasShopName && hasValidRoute && hasOwner && hasPayoutUser;
};

function AddShopDialog({ onClose }) {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const formRef = React.useRef(null);
  const stateRef = React.useRef(state);
  stateRef.current = state;

  const handleClose = React.useCallback(() => {
    onClose(() => {}, { shouldRefresh: false });
  }, [onClose]);

  const handleOpenChange = React.useCallback(
    (open) => {
      if (!open && !stateRef.current.submitting) {
        handleClose();
      }
    },
    [handleClose],
  );

  const handleSubmit = React.useCallback(
    async (e) => {
      e.preventDefault();

      if (!formRef.current.reportValidity()) return;

      const s = stateRef.current;

      if (!isFormValid(s)) return;

      dispatch({ type: ACTION.SUBMIT_START });

      const payload = {
        shopName: s.shopName.trim(),
        shopRoute: finalizeShopRouteInput(s.shopRoute),
        ownerUserId: s.ownerUser.user_id,
        shopType: s.shopType,
        payoutUserId: s.payoutSameAsOwner
          ? s.ownerUser.user_id
          : s.payoutUser.user_id,
        shipping: s.shipping,
        verticalId: s.vertical?.id ?? null,
        subverticalId: s.subvertical?.id ?? null,
      };

      try {
        const response = await window.fetch("/api/influencer/adminCreateShop", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok) {
          const message = data.message || "Failed to create shop";
          const isRouteError =
            response.status === 400 &&
            /route.*(taken|exists|in use)/i.test(message);

          if (isRouteError) {
            dispatch({
              type: ACTION.SET_ROUTE_ERROR,
              error: "This route is already taken",
            });
          }

          dispatch({
            type: ACTION.SUBMIT_ERROR,
            error: message,
          });
          return;
        }

        dispatch({ type: ACTION.SUBMIT_SUCCESS });
        onClose(() => {}, { shouldRefresh: true, route: data.route });
      } catch (err) {
        dispatch({
          type: ACTION.SUBMIT_ERROR,
          error: err.message || "An unexpected error occurred",
        });
      }
    },
    [onClose],
  );

  return (
    <Dialog.Root open onOpenChange={handleOpenChange}>
      <Dialog.Popup size="md">
        <Dialog.Title>Add shop</Dialog.Title>
        <Dialog.Close />

        <form
          ref={formRef}
          className={styles.form}
          noValidate
          onSubmit={handleSubmit}
        >
          <Dialog.Body>
            <div className={styles.formBody}>
              <ShopDetailsSection
                shopName={state.shopName}
                shopRoute={state.shopRoute}
                routeError={state.routeError}
                dispatch={dispatch}
              />

              <OwnerSection
                ownerEmail={state.ownerEmail}
                ownerUser={state.ownerUser}
                ownerStatus={state.ownerStatus}
                dispatch={dispatch}
              />

              <ShopTypeSection shopType={state.shopType} dispatch={dispatch} />

              <PayoutSection
                payoutSameAsOwner={state.payoutSameAsOwner}
                payoutEmail={state.payoutEmail}
                payoutUser={state.payoutUser}
                payoutStatus={state.payoutStatus}
                dispatch={dispatch}
              />

              <ShippingSection shipping={state.shipping} dispatch={dispatch} />

              <VerticalSection
                vertical={state.vertical}
                subvertical={state.subvertical}
                dispatch={dispatch}
              />

              {state.error && <Alert severity="error">{state.error}</Alert>}
            </div>
          </Dialog.Body>

          <Dialog.Actions>
            <Button
              variant="text"
              onClick={handleClose}
              disabled={state.submitting}
            >
              Cancel
            </Button>
            <Button type="submit" variant="filled" loading={state.submitting}>
              Create Shop
            </Button>
          </Dialog.Actions>
        </form>
      </Dialog.Popup>
    </Dialog.Root>
  );
}

AddShopDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default AddShopDialog;
